import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useRecoilState, useResetRecoilState } from "recoil";
import GroupIcon from "@mui/icons-material/Group";
import ReactLoading from "react-loading";
import { allPlantsFullAtom, isLoadingAtom } from "../../../recoil/atoms";
import { IPlantFull, IPlantUser } from "../../../common-interfaces/interfaces";
import { deepCopy } from "../../../helpers/utils";
import { useApiWrapper } from "../../../hooks/useApiWrapper";
import { wizardSelectedPlantAtom } from "../../../recoil/CreateInstallationAtoms";
import LinkInstallationToClient from "../common/LinkInstallationToClient";

const EditClients: FC<{
  plantID: number;
  text?: string;
  isButton?: boolean
}> = ({ plantID, text, isButton }) => {
  const [allPlantsFull, setAllPlantFull] = useRecoilState(allPlantsFullAtom);
  const [selectedPlant, setSelectedPlant] = useRecoilState<IPlantFull | undefined>(wizardSelectedPlantAtom);
  const [isLoading] = useRecoilState<boolean>(isLoadingAtom);

  const [isFetching, setIsFetching] = useState(false);
  const [open, setOpen] = useState(false);

  const apiWrapper = useApiWrapper();
  const resetSp = useResetRecoilState(wizardSelectedPlantAtom);

  const init = async () => {
    let plantFull: IPlantFull | undefined = allPlantsFull.find((pf) => {
      if (pf.installationName === undefined) return;
      return pf.plant.id === plantID;
    });
    setSelectedPlant(plantFull);
  };

  const handleDialogOpen = () => {
    resetSp();
    init();
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleEditPlant = async (): Promise<IPlantUser[]> => {
    let tempSelectedPlant: IPlantFull = await deepCopy(selectedPlant);
    tempSelectedPlant.clients = await deepCopy(selectedPlant?.clients);
    let tmpAllPlant: IPlantFull[] = await deepCopy(allPlantsFull);
    tmpAllPlant = tmpAllPlant.map((pf) => {
      if (pf.plant.id === plantID) {
        return tempSelectedPlant;
      }
      return pf;
    });
    setAllPlantFull(tmpAllPlant);
    const url = "api/plant/edituserplantandinstallation";
    let client = await apiWrapper.postWithAuth(url, tempSelectedPlant);
    return client.data.clients;
  };

  const handleConfirm = async () => {
    setIsFetching(true);
    await handleEditPlant();
    handleDialogClose();
    setIsFetching(false);
  };

  return (
    <>
      {!isButton ? <MenuItem
        onClick={handleDialogOpen}
        disabled={isLoading}
      >
        <ListItemIcon><GroupIcon /></ListItemIcon>
        <ListItemText>{text}</ListItemText>
      </MenuItem> :
        <Tooltip
          title={"EDIT CLIENTS"}
          placement="top"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <Button
            onClick={handleDialogOpen}
            variant="contained"
            disabled={isLoading}
          >
            <GroupIcon />
          </Button>
        </Tooltip>}
      <Dialog open={open} fullWidth onClose={handleDialogClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'background.default'
            }
          }
        }}>
        <DialogTitle>Edit {selectedPlant?.plant?.name} clients</DialogTitle>
        <DialogContent>
          {isFetching ? (
            <Box
              height={"180px"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <ReactLoading type={"bars"} width={"5vw"} />
            </Box>
          ) : (
            <>
              <DialogContentText mt={2}>
                <Box display={"flex"}>
                  <Typography mr={2}>Current selected clients: </Typography>
                  <Box>
                    {selectedPlant?.clients &&
                      selectedPlant?.clients.length > 0 &&
                      selectedPlant.clients.map((client) => (
                        <Typography key={client.user.email}>
                          {client.user.first_name} {client.user.last_name}
                        </Typography>
                      ))}
                  </Box>
                </Box>
              </DialogContentText>
              <Box className="center" mb="20px">
                <LinkInstallationToClient></LinkInstallationToClient>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={isFetching || (!plantID && !selectedPlant?.clients)}
            onClick={handleConfirm}
            sx={{
              m: 2,
              px: 4,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditClients;
